/**
 * One loading/error/data state machine for every screen.
 *
 * WHY: each page reads one or two endpoints and must show a real loading, empty and error
 * state (CLAUDE.md: the live account has no payouts yet, so empty is the common case). Doing
 * that per page invites four subtly different spinners; doing it here makes the states uniform
 * and keeps `AbortController` bookkeeping in a single place so a store switch cannot land a
 * stale response on the new screen.
 */
import { useCallback, useEffect, useState } from "react";

export type AsyncState<T> =
  | { status: "loading"; data: null; error: null }
  | { status: "ready"; data: T; error: null }
  | { status: "error"; data: null; error: Error };

const LOADING: AsyncState<never> = { status: "loading", data: null, error: null };

export function useAsync<T>(load: (signal: AbortSignal) => Promise<T>, deps: readonly unknown[]): AsyncState<T> & { reload: () => void } {
  const [state, setState] = useState<AsyncState<T>>(LOADING);
  const [nonce, setNonce] = useState(0);
  // The caller's closure is new on every render, so `deps` — not the function — is the real
  // identity of the request. That is exactly what useCallback's dependency list is for.
  const run = useCallback(load, deps);

  useEffect(() => {
    const controller = new AbortController();
    setState(LOADING);
    run(controller.signal).then(
      (data) => { if (!controller.signal.aborted) setState({ status: "ready", data, error: null }); },
      (error: unknown) => {
        if (controller.signal.aborted) return;
        setState({ status: "error", data: null, error: error instanceof Error ? error : new Error(String(error)) });
      },
    );
    return () => controller.abort();
  }, [run, nonce]);

  return { ...state, reload: useCallback(() => setNonce((n) => n + 1), []) };
}
